<template>
  <div>
    <span style="color: #2196f3">
      {{ $t("rentals.rental_contract") }}
    </span>
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      height="2"
      class="mb-2"
    ></v-progress-linear>
    <v-row class="mt-n1">
      <v-col cols="12" sm="6" md="auto" class="pt-0 pb-0 rental-field-col">
        <v-combobox
          v-model="selectedProperty"
          :items="propertiesList"
          item-value="id"
          item-text="name"
          class="to-upper"
          :label="$t('rentals.property_name')"
          return-object
          :outlined="isBoxes"
          :dense="isBoxes"
          :disabled="disabled"
          :loading="propertiesLoading"
          @change="onPropertyChanged"
        >
          <template slot="selection" slot-scope="{ item }">
            {{ getPropertyName(item) }}
          </template>
          <template slot="item" slot-scope="{ item }">
            <v-list-item-content>
              <p class="client_name">
                {{ getPropertyName(item) }}
              </p>
            </v-list-item-content>
          </template>
        </v-combobox>
      </v-col>
      <v-col
        cols="12"
        sm="6"
        md="auto"
        class="pt-0 pb-0 rental-field-col"
        v-if="identifier"
      >
        <v-text-field
          v-model="identifier"
          :label="$t('rentals.identifier')"
          readonly
          :outlined="isBoxes"
          :dense="isBoxes"
          disabled
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" md="auto" class="pt-0 pb-0 rental-field-col">
        <DateInput
          v-model="startDateLocal"
          :label="$t('rentals.start_date')"
          :disabled="false"
          :clearable="false"
          @change="onStartDateChanged"
        />
      </v-col>
      <v-col cols="12" sm="6" md="auto" class="pt-0 pb-0 rental-field-col">
        <DateInput
          v-model="endDateLocal"
          :label="$t('rentals.end_date')"
          :disabled="false"
          :clearable="false"
          :min="endDateMin"
          @change="onEndDateChanged"
        />
      </v-col>
      <v-col cols="12" sm="6" md="auto" class="pt-0 pb-0 rental-field-col">
        <v-text-field
          v-model="contract_reference"
          :label="$t('rentals.reference')"
          :outlined="isBoxes"
          :dense="isBoxes"
          @change="
            $emit(
              'update:comp_contract_reference',
              contract_reference
            )
          "
          @keyup="
            $emit(
              'update:comp_contract_reference',
              contract_reference
            )
          "
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" md="auto" class="pt-0 pb-0 rental-field-col">
        <v-menu
          v-model="colorMenu"
          nudge-bottom="105"
          nudge-left="16"
          :close-on-content-click="false"
          hide-details
        >
          <template v-slot:activator="{ on }">
            <div class="color-picker-wrapper">
              <div :style="swatchStyle" v-on="on" />
              <v-text-field
                :value="colorLocal"
                :label="$t('rentals.appointment_color')"
                readonly
                :outlined="isBoxes"
                :dense="isBoxes"
                :disabled="disabled"
                hide-details
                v-on="on"
              />
            </div>
          </template>
          <v-card>
            <v-card-text class="pa-0">
              <v-color-picker
                v-model="colorLocal"
                mode="hexa"
                class="mt-10"
                flat
                @input="onColorChanged"
              />
            </v-card-text>
          </v-card>
        </v-menu>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import axios from "@/plugins/axios";
import { API_BASE_URL } from "@/config";
import DateInput from "@/components/DateInput.vue";

export default {
  name: "PropertyRentalInfoSection",
  components: {
    DateInput,
  },
  props: {
    comp_rental: {
      type: Object,
      default: null,
    },
    comp_selected_bill: {
      type: [Object, String],
      default: null,
    },
    comp_property_id: {
      type: Number,
      default: null,
    },
    comp_identifier: {
      type: String,
      default: "",
    },
    comp_start_date: {
      type: String,
      default: "",
    },
    comp_end_date: {
      type: String,
      default: "",
    },
    comp_note: {
      type: String,
      default: "",
    },
    comp_contract_reference: {
      type: String,
      default: "",
    },
    comp_color: {
      type: String,
      default: null,
    },
    rental: {
      type: Object,
      default: null,
    },
    properties: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isBoxes: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localProperties: [],
      propertiesLoading: false,
      selectedProperty: null,
      isSyncing: false,
      startDateLocal: null,
      endDateLocal: null,
      contract_reference: null,
      identifier: null,
      colorLocal: null,
      colorMenu: false,
    };
  },
  mounted() {
    if (!this.properties || this.properties.length === 0) {
      this.fetchProperties();
    }
    this.colorLocal = this.comp_color || null;
  },
  watch: {
    rental: {
      immediate: true,
      handler(newVal) {
        this.syncFromRental(newVal);
      },
    },
    comp_rental: {
      immediate: true,
      handler(newVal) {
        this.syncFromRental(newVal);
      },
    },
    comp_start_date(newVal) {
      if (!this.isSyncing) {
        this.startDateLocal = newVal || null;
      }
    },
    comp_identifier(newVal) {
      if (!this.isSyncing) {
        this.identifier = newVal || null;
      }
    },
    comp_contract_reference(newVal) {
      if (!this.isSyncing) {
        this.contract_reference = newVal || null;
      }
    },
    comp_end_date(newVal) {
      if (!this.isSyncing) {
        this.endDateLocal = newVal || null;
      }
    },
    comp_color(newVal) {
      if (!this.isSyncing) {
        this.colorLocal = newVal || null;
      }
    },
    properties(newVal) {
      if (newVal && newVal.length > 0) {
        this.localProperties = [];
      } else if (this.localProperties.length === 0) {
        this.fetchProperties();
      }
    },
  },
  computed: {
    propertiesList() {
      return this.properties && this.properties.length > 0
        ? this.properties
        : this.localProperties;
    },
    activeRental() {
      return this.comp_rental || this.rental || null;
    },
    endDateMin() {
      const start = this.startDateLocal;
      if (!start) return null;
      return (typeof start === "string" ? start : String(start)).split(" ")[0];
    },
    swatchStyle() {
      return {
        backgroundColor: this.colorLocal || "#e0e0e0",
        cursor: "pointer",
        height: "30px",
        width: "30px",
        borderRadius: "4px",
        minWidth: "30px",
      };
    },
  },
  methods: {
    syncFromRental(rentalData) {
      if (!rentalData) {
        return;
      }
      this.isSyncing = true;
      this.selectedProperty = this.resolvePropertyFromRental(rentalData);
      this.startDateLocal = rentalData?.start_date || this.comp_start_date || null;
      this.endDateLocal = rentalData?.end_date || this.comp_end_date || null;
      this.contract_reference = rentalData?.contract_reference || this.comp_contract_reference || null;
      this.identifier = rentalData?.identifier || this.comp_identifier || null;
      this.$nextTick(() => {
        this.isSyncing = false;
      });
    },
    onColorChanged(value) {
      if (this.isSyncing) return;
      this.$emit("update:comp_color", value || null);
    },
    emitRentalUpdate(rentalData) {
      this.$emit("update:comp_rental", rentalData);
    },
    fetchProperties() {
      this.propertiesLoading = true;
      axios
        .get(API_BASE_URL + "/properties", {
          headers: {
            Authorization: "Bearer " + this.$store.state.AccessToken,
          },
        })
        .then(({ data }) => {
          this.localProperties = data?.data || [];
        })
        .catch(() => {
          this.localProperties = [];
        })
        .finally(() => {
          this.propertiesLoading = false;
        });
    },
    resolvePropertyFromRental(rental) {
      const rentalData = rental || {};
      if (rentalData.property) {
        return rentalData.property;
      }
      if (rentalData.property_id) {
        return (
          this.propertiesList.find(
            (property) => property.id === rentalData.property_id
          ) ||
          rentalData.property_name ||
          null
        );
      }
      return rentalData.property_name || rentalData.property || null;
    },
    onPropertyChanged(value) {
      if (this.isSyncing) {
        return;
      }
      const rentalData = { ...(this.activeRental || {}) };
      if (value && typeof value === "object") {
        rentalData.property = value;
        rentalData.property_id = value.id || null;
        rentalData.property_name = this.getPropertyName(value);
        // Update appointment color to property color when property has one
        const propertyColor = value.color;
        if (propertyColor) {
          const hexColor = String(propertyColor).startsWith("#")
            ? propertyColor
            : "#" + String(propertyColor).replace(/^#/, "");
          this.colorLocal = hexColor;
          this.$emit("update:comp_color", hexColor);
        }
      } else {
        rentalData.property = null;
        rentalData.property_id = null;
        rentalData.property_name = value || null;
      }
      this.emitRentalUpdate(rentalData);
    },
    onStartDateChanged(value) {
      if (this.isSyncing) {
        return;
      }
      const startDatePart = value ? String(value).split(" ")[0] : null;
      const endDatePart = this.endDateLocal
        ? String(this.endDateLocal).split(" ")[0]
        : null;
      if (startDatePart && endDatePart && endDatePart < startDatePart) {
        this.endDateLocal = startDatePart;
        this.$emit("update:comp_end_date", startDatePart);
      }
      const rentalData = { ...(this.activeRental || {}) };
      rentalData.start_date = value;
      this.$emit("update:comp_start_date", value);
      this.emitRentalUpdate(rentalData);
    },
    onEndDateChanged(value) {
      if (this.isSyncing) {
        return;
      }
      const startDatePart = this.startDateLocal
        ? String(this.startDateLocal).split(" ")[0]
        : null;
      const endDatePart = value ? String(value).split(" ")[0] : null;
      if (startDatePart && endDatePart && endDatePart < startDatePart) {
        this.$toast.error(this.$t("rentals.end_date_cannot_be_before_start"));
        const correctedEnd = (this.startDateLocal || "").split(" ")[0];
        this.endDateLocal = correctedEnd;
        this.$emit("update:comp_end_date", correctedEnd);
        const rentalData = { ...(this.activeRental || {}) };
        rentalData.end_date = correctedEnd;
        this.emitRentalUpdate(rentalData);
        return;
      }
      const rentalData = { ...(this.activeRental || {}) };
      rentalData.end_date = value;
      this.$emit("update:comp_end_date", value);
      this.emitRentalUpdate(rentalData);
    },
    getPropertyName(property) {
      if (!property) {
        return "";
      }
      if (typeof property === "string") {
        return property;
      }
      return property.name || property.property_name || "";
    },
  },
};
</script>

<style scoped>
.rental-field-col {
  flex: 1 1 180px;
  max-width: 100%;
}
.color-picker-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}
.color-picker-wrapper .v-text-field {
  flex: 1;
}
</style>
